import { useMemo, useState } from 'react';
import { Collapse, Empty, Input, Select, Space, Tag, Typography } from 'antd';
import type {
  IamEffect,
  IamResourceScope,
  PolicyPermissionItem,
  PolicyPermissionRuleView,
} from '@/pages/iam/composables/usePolicyPermissionItems';
import './PolicyPermissionView.css';

interface PermissionStatementView extends PolicyPermissionRuleView {
  key: string;
  actionCodes: string[];
}

interface PermissionGroupView {
  value: string;
  label: string;
  statements: PermissionStatementView[];
}

interface Props {
  items: PolicyPermissionItem[];
  emptyDescription?: string;
  showFilters?: boolean;
}

export function PolicyPermissionView({
  items,
  emptyDescription = '没有匹配的权限配置',
  showFilters = true,
}: Props) {
  const effectLabel: Record<IamEffect, string> = { Allow: '允许', Deny: '拒绝' };
  const scopeLabel: Record<IamResourceScope, string> = {
    OWN: '我创建的资源',
    ALL: '租户全部资源',
  };
  const [search, setSearch] = useState('');
  const [groupValue, setGroupValue] = useState<string>();
  const [effect, setEffect] = useState<IamEffect>();
  const [scope, setScope] = useState<IamResourceScope>();

  const groupOptions = useMemo(() => {
    const labelsByValue = new Map<string, string>();
    items.forEach((item) => labelsByValue.set(item.groupValue, item.groupLabel));
    return [...labelsByValue].map(([value, label]) => ({ value, label }));
  }, [items]);

  const groups = useMemo<PermissionGroupView[]>(() => {
    const groupedStatements = new Map<
      string,
      { label: string; statements: Map<string, PermissionStatementView> }
    >();
    const normalizedSearch = search.trim().toLowerCase();

    items.forEach((item) => {
      if (groupValue && item.groupValue !== groupValue) return;
      if (
        normalizedSearch &&
        !item.name.toLowerCase().includes(normalizedSearch) &&
        !item.groupLabel.toLowerCase().includes(normalizedSearch) &&
        !item.code.toLowerCase().includes(normalizedSearch)
      ) {
        return;
      }

      item.rules.forEach((rule) => {
        if (effect && rule.effect !== effect) return;
        if (scope && rule.resourceScope !== scope) return;

        const group = groupedStatements.get(item.groupValue) ?? {
          label: item.groupLabel,
          statements: new Map<string, PermissionStatementView>(),
        };
        const key = `${rule.effect}:${rule.resourceScope}`;
        const statement = group.statements.get(key) ?? {
          key,
          actionCodes: [],
          effect: rule.effect,
          resourceScope: rule.resourceScope,
          sources: [],
        };
        if (!statement.actionCodes.includes(item.code)) {
          statement.actionCodes.push(item.code);
        }
        rule.sources?.forEach((source) => {
          if (!statement.sources?.some((item) => item.key === source.key)) {
            statement.sources?.push(source);
          }
        });
        group.statements.set(key, statement);
        groupedStatements.set(item.groupValue, group);
      });
    });

    return [...groupedStatements].map(([value, group]) => ({
      value,
      label: group.label,
      statements: [...group.statements.values()],
    }));
  }, [effect, groupValue, items, scope, search]);

  const itemsByCode = useMemo(() => new Map(items.map((item) => [item.code, item])), [items]);

  return (
    <div className="iam-policy-permissions">
      {showFilters && (
        <div className="iam-policy-permissions-filters">
          <Input
            allowClear
            placeholder="搜索权限名称 / 分组 / code"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Select
            allowClear
            placeholder="全部服务"
            value={groupValue}
            onChange={setGroupValue}
            options={groupOptions}
          />
          <Select
            allowClear
            placeholder="权限类型"
            value={effect}
            onChange={setEffect}
            options={[
              { value: 'Allow', label: '允许' },
              { value: 'Deny', label: '拒绝' },
            ]}
          />
          <Select
            allowClear
            placeholder="资源范围"
            value={scope}
            onChange={setScope}
            options={[
              { value: 'OWN', label: '我创建的资源' },
              { value: 'ALL', label: '租户全部资源' },
            ]}
          />
        </div>
      )}

      {groups.length ? (
        <Collapse
          bordered={false}
          className="iam-policy-permissions-groups"
          defaultActiveKey={groups.map((group) => group.value)}
          items={groups.map((group) => ({
            key: group.value,
            label: (
              <div className="iam-policy-permissions-group-head">
                <Typography.Text strong>{group.label}</Typography.Text>
                <Typography.Text type="secondary">
                  {new Set(group.statements.flatMap((statement) => statement.actionCodes)).size}{' '}
                  个权限点
                </Typography.Text>
              </div>
            ),
            children: [...group.statements]
              .sort(
                (left, right) => Number(right.effect === 'Deny') - Number(left.effect === 'Deny')
              )
              .map(({ key, actionCodes, effect: statementEffect, resourceScope, sources = [] }) => (
                <div className="iam-policy-permissions-statement" key={`${group.value}-${key}`}>
                  <Space wrap>
                    <Tag color={statementEffect === 'Allow' ? 'success' : 'error'}>
                      {effectLabel[statementEffect]}
                    </Tag>
                    <Typography.Text type="secondary">
                      资源范围：{scopeLabel[resourceScope]}
                    </Typography.Text>
                    {sources.length > 0 && (
                      <>
                        <Typography.Text type="secondary">来源：</Typography.Text>
                        {sources.map((source) => (
                          <Tag bordered={false} color="blue" key={source.key}>
                            {source.label}
                          </Tag>
                        ))}
                      </>
                    )}
                  </Space>
                  <div className="iam-policy-permissions-action-list">
                    {actionCodes.map((code) => {
                      const item = itemsByCode.get(code);
                      return (
                        <div key={code}>
                          <strong>{item?.name || (code === '*' ? '全部权限点' : code)}</strong>
                          <span>{item?.groupLabel || group.label}</span>
                          <code>{code}</code>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )),
          }))}
        />
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyDescription} />
      )}
    </div>
  );
}
